import { useEffect, useState } from "react";
import { IndianRupee, Loader2, Plus, Search, Trash2, X } from "lucide-react";
import useSetting from "../../Sevices/useSetting";

const initialForm = {
  userId: "",
  minimumDistance: "",
  minimumOrderAmount: "",
  deliveryCharges: "",
  perKmCharges: "",
};

const DeliveryCharges = () => {
  const {
    deliveryCharges,
    deliveryChargesLoading,
    fetchDeliveryCharges,
    addDeliveryCharges,
    deleteDeliveryCharges,
  } = useSetting();

  const [form, setForm] = useState(initialForm);
  const [searchRestaurantId, setSearchRestaurantId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchDeliveryCharges("");
  }, [fetchDeliveryCharges]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "userId") {
      setForm((previous) => ({
        ...previous,
        [name]: value.toUpperCase().replace(/\s/g, ""),
      }));

      return;
    }

    if (value !== "" && Number(value) < 0) {
      return;
    }

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const restaurantId = form.userId.trim();
    const minimumDistance = Number(form.minimumDistance);
    const minimumOrderAmount = Number(form.minimumOrderAmount);
    const deliveryChargeAmount = Number(form.deliveryCharges);
    const perKmChargeAmount = Number(form.perKmCharges);

    if (!restaurantId) {
      return "Restaurant ID is required";
    }

    if (restaurantId.length < 2) {
      return "Please enter a valid Restaurant ID";
    }

    if (form.minimumDistance === "") {
      return "Minimum distance is required";
    }

    if (!Number.isFinite(minimumDistance)) {
      return "Please enter a valid minimum distance";
    }

    if (minimumDistance < 0) {
      return "Minimum distance cannot be negative";
    }

    if (form.minimumOrderAmount === "") {
      return "Minimum order amount is required";
    }

    if (!Number.isFinite(minimumOrderAmount)) {
      return "Please enter a valid minimum order amount";
    }

    if (minimumOrderAmount < 0) {
      return "Minimum order amount cannot be negative";
    }

    if (form.deliveryCharges === "") {
      return "Delivery charges are required";
    }

    if (!Number.isFinite(deliveryChargeAmount)) {
      return "Please enter valid delivery charges";
    }

    if (deliveryChargeAmount < 0) {
      return "Delivery charges cannot be negative";
    }

    if (form.perKmCharges === "") {
      return "Per KM charges are required";
    }

    if (!Number.isFinite(perKmChargeAmount)) {
      return "Please enter valid per KM charges";
    }

    if (perKmChargeAmount < 0) {
      return "Per KM charges cannot be negative";
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      return;
    }

    const success = await addDeliveryCharges(form);

    if (success) {
      setForm(initialForm);
      setShowForm(false);
    }
  };

  const handleSearch = async (event) => {
    event.preventDefault();

    await fetchDeliveryCharges(searchRestaurantId.trim());
  };

  const handleResetSearch = async () => {
    setSearchRestaurantId("");
    await fetchDeliveryCharges("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem?.pk_charges_id) return;

    setDeletingId(deleteItem.pk_charges_id);

    try {
      const success = await deleteDeliveryCharges(deleteItem.pk_charges_id);

      if (success) {
        setDeleteItem(null);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const formatAmount = (value) => {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return "0.00";
    }

    return amount.toFixed(2);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Delivery Charges
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Manage restaurant minimum distance and delivery charges.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setShowForm((previous) => !previous);
            setForm(initialForm);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          {showForm ? <X size={17} /> : <Plus size={17} />}

          {showForm ? "Close Form" : "Add Delivery Charges"}
        </button>
      </div>

      {/* Search */}
      <form
        onSubmit={handleSearch}
        className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div className="min-w-[240px] flex-1">
          <label className="mb-1.5 block text-sm font-medium text-gray-700">
            Search by Restaurant ID
          </label>

          <input
            type="text"
            value={searchRestaurantId}
            onChange={(event) =>
              setSearchRestaurantId(
                event.target.value.toUpperCase().replace(/\s/g, ""),
              )
            }
            placeholder="Enter restaurant ID"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        <button
          type="submit"
          disabled={deliveryChargesLoading}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Search size={17} />
          Search
        </button>

        <button
          type="button"
          onClick={handleResetSearch}
          disabled={deliveryChargesLoading}
          className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Reset
        </button>
      </form>

      {/* Add Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
        >
          <h3 className="mb-5 text-base font-semibold text-gray-900">
            Add Delivery Charges
          </h3>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Restaurant ID <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                name="userId"
                value={form.userId}
                onChange={handleChange}
                placeholder="A1001"
                maxLength={30}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Minimum Distance (KM)
                <span className="text-red-500"> *</span>
              </label>

              <input
                type="number"
                name="minimumDistance"
                value={form.minimumDistance}
                onChange={handleChange}
                placeholder="10"
                min="0"
                max="100000"
                step="0.01"
                required
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-"].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Minimum Order Amount
                <span className="text-red-500"> *</span>
              </label>

              <div className="relative">
                <IndianRupee
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="number"
                  name="minimumOrderAmount"
                  value={form.minimumOrderAmount}
                  onChange={handleChange}
                  placeholder="300"
                  min="0"
                  max="10000000"
                  step="0.01"
                  required
                  onKeyDown={(event) => {
                    if (["e", "E", "+", "-"].includes(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Delivery Charges
                <span className="text-red-500"> *</span>
              </label>

              <div className="relative">
                <IndianRupee
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="number"
                  name="deliveryCharges"
                  value={form.deliveryCharges}
                  onChange={handleChange}
                  placeholder="50"
                  min="0"
                  max="10000000"
                  step="0.01"
                  required
                  onKeyDown={(event) => {
                    if (["e", "E", "+", "-"].includes(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Per KM Charges
                <span className="text-red-500"> *</span>
              </label>

              <div className="relative">
                <IndianRupee
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="number"
                  name="perKmCharges"
                  value={form.perKmCharges}
                  onChange={handleChange}
                  placeholder="5"
                  min="0"
                  max="10000000"
                  step="0.01"
                  required
                  onKeyDown={(event) => {
                    if (["e", "E", "+", "-"].includes(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />
              </div>
            </div>
          </div>

          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setForm(initialForm);
                setShowForm(false);
              }}
              disabled={deliveryChargesLoading}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={deliveryChargesLoading}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deliveryChargesLoading && (
                <Loader2 size={17} className="animate-spin" />
              )}
              Save Charges
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  ID
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  Restaurant ID
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  Minimum Distance
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  Minimum Order
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  Delivery Charge
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                  Per KM Charge
                </th>

                <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                  Action
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {deliveryChargesLoading && deliveryCharges.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <Loader2 size={20} className="animate-spin" />
                      Loading delivery charges...
                    </div>
                  </td>
                </tr>
              ) : deliveryCharges.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-12 text-center text-sm text-gray-500"
                  >
                    No delivery charges found.
                  </td>
                </tr>
              ) : (
                deliveryCharges.map((item) => (
                  <tr
                    key={item.pk_charges_id}
                    className="transition hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {item.pk_charges_id ?? "-"}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900">
                      {item.restaurant_id || "-"}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {item.minimum_distance ?? 0} KM
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      ₹{formatAmount(item.minimum_amount)}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      ₹{formatAmount(item.delivery_charges)}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      ₹{formatAmount(item.per_km_charges)}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setDeleteItem(item)}
                        disabled={deliveryChargesLoading}
                        className="inline-flex items-center justify-center rounded-lg bg-red-50 p-2 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                        title="Delete delivery charges"
                      >
                        <Trash2 size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => {
            if (!deletingId) {
              setDeleteItem(null);
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Delivery Charges
                </h3>

                <p className="mt-2 text-sm leading-6 text-gray-500">
                  Are you sure you want to delete delivery charges for{" "}
                  <span className="font-semibold text-gray-800">
                    {deleteItem.restaurant_id || "-"}
                  </span>
                  ?
                </p>
              </div>

              <button
                type="button"
                onClick={() => setDeleteItem(null)}
                disabled={Boolean(deletingId)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <X size={19} />
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteItem(null)}
                disabled={Boolean(deletingId)}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={Boolean(deletingId)}
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Trash2 size={17} />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryCharges;
